<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'JobView',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    item: {
      type: Object,
      required: true,
    },
    noDisplayHeader: {
      type: Boolean,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);

  const $data = computed(() => ({
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const hasFiles = computed(() => {
    return props.item.files && props.item.files.length;
  });
  const hasChildJobs = computed(() => {
    return props.item.jobs && props.item.jobs.length;
  });

  watch(
    () => props.item,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('item');
    }
  );
  watch(
    () => props.noDisplayHeader,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('noDisplayHeader');
    }
  );

  const actions = {};

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpBox direction="column" class="h-100 overflow-hidden">
    <table>
      <tbody>
        <tr>
          <td>
            {{ pgettext('Job', 'Job ID') }}
          </td>
          <td class="px-3">
            {{ item.id }}
          </td>
        </tr>
        <tr>
          <td>
            {{ pgettext('Job', 'Job name') }}
          </td>
          <td class="px-3">
            {{ item.name }}
          </td>
        </tr>
        <tr>
          <td>
            {{ pgettext('Job', 'Submitter') }}
          </td>
          <td class="px-3">
            {{ username(item.created_by) }}
          </td>
        </tr>
        <tr>
          <td>
            {{ pgettext('Job', 'Submit time') }}
          </td>
          <td class="px-3">
            {{ datetime(item.submit_time) }}
          </td>
        </tr>
        <tr v-if="item.execution_time">
          <td>
            {{ pgettext('Job', 'Execution time') }}
          </td>
          <td class="px-3">
            {{ $fn.humanizeDuration(item.execution_time) }}
          </td>
        </tr>
        <tr v-if="item.message">
          <td>
            {{ pgettext('Job', 'Message') }}
          </td>
          <td class="px-3">
            {{ item.message }}
          </td>
        </tr>
      </tbody>
    </table>
    <hr />
    <JobFiles :item="item" v-if="hasFiles"> </JobFiles>
    <hr v-if="hasFiles" />
    <ChildJobs :parent="item" v-if="hasChildJobs"> </ChildJobs>
    <JobRuns :item="item"> </JobRuns>
  </ItpBox>
</template>
